import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ConfirmDialog } from './ConfirmDialog';
import { Plus, Save, X, Baby, CheckCircle, AlertCircle } from 'lucide-react';
import { AddChildRequest } from '../types/family.types';
import { Gender, UserStatus } from '../types/enums';
import { useFamilyMutations } from '../hooks/useFamilyMutations';
import { getGenderOptions, getMonthOptions } from '../utils/form-options.utils';

interface AddChildCardProps {
  familyId: string;
  familyStatus?: UserStatus;
}

const initialFormData: AddChildRequest & { gender: Gender } = {
  firstName: '',
  lastName: '',
  birthYear: new Date().getFullYear(),
  birthMonth: 1,
  gender: Gender.PREFER_NOT_TO_SAY,
  hasSpecialNeeds: false,
  consentForPhoto: false,
};

export const AddChildCard = ({ familyId, familyStatus }: AddChildCardProps) => {
  const { t } = useTranslation('registration');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());

  const { addChild } = useFamilyMutations(familyId);

  const getErrorClass = (id: string) => (formErrors.has(id) ? 'border-red-500 bg-red-50/10' : '');

  const getErrorMessage = (id: string) => formErrors.get(id) || '';

  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const errorBanner = document.getElementById('error-banner-child');
      if (errorBanner) {
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        element.focus({ preventScroll: true });
      }, 300);
    }
  };

  const validateForm = (): { isValid: boolean; firstErrorId?: string } => {
    const errors = new Map<string, string>();
    let firstErrorId: string | undefined;

    const addError = (id: string, message: string) => {
      errors.set(id, message);
      if (!firstErrorId) firstErrorId = id;
    };

    // First Name validation
    if (!formData.firstName.trim()) {
      addError('add-child-firstName', t('validation.enterNameFor', { label: t('labels.child') }));
    } else if (formData.firstName.trim().length < 2) {
      addError('add-child-firstName', t('validation.nameTooShort'));
    } else if (formData.firstName.length > 100) {
      addError('add-child-firstName', t('validation.nameTooLong'));
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      addError('add-child-lastName', t('validation.enterNameFor', { label: t('labels.child') }));
    } else if (formData.lastName.trim().length < 2) {
      addError('add-child-lastName', t('validation.nameTooShort'));
    } else if (formData.lastName.length > 100) {
      addError('add-child-lastName', t('validation.nameTooLong'));
    }

    // Birth year and month are required and have defaults, so always check they're valid
    if (!formData.birthYear || formData.birthYear < 1900 || formData.birthYear > new Date().getFullYear()) {
      addError('add-child-birthYear', t('validation.enterBirthFor', { label: t('labels.child') }));
    }

    if (!formData.birthMonth || formData.birthMonth < 1 || formData.birthMonth > 12) {
      addError('add-child-birthMonth', t('validation.enterBirthFor', { label: t('labels.child') }));
    }

    setFormErrors(errors);
    return { isValid: errors.size === 0, firstErrorId };
  };

  const handleSave = async () => {
    const { isValid, firstErrorId } = validateForm();
    if (!isValid) {
      if (firstErrorId) scrollToError(firstErrorId);
      return;
    }

    if (familyStatus === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set('add-child-firstName', t('toast.familyPendingCannotAddMembers'));
      setFormErrors(apiErrors);
      scrollToError('add-child-firstName');
      return;
    }
    const request: AddChildRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      birthYear: formData.birthYear,
      birthMonth: formData.birthMonth,
      gender: formData.gender,
      hasSpecialNeeds: formData.hasSpecialNeeds,
      consentForPhoto: formData.consentForPhoto,
    };

    try {
      await addChild.mutateAsync(request);
      setFormData(initialFormData);
      setFormErrors(new Map());
      setIsExpanded(false);
    } catch (error) {
      const apiErrors = new Map<string, string>();
      const errorMsg =
        error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');

      if (errorMsg.includes('First name') || errorMsg.includes('firstName')) {
        apiErrors.set('add-child-firstName', errorMsg);
      } else if (errorMsg.includes('Last name') || errorMsg.includes('lastName')) {
        apiErrors.set('add-child-lastName', errorMsg);
      } else if (errorMsg.includes('Birth year') || errorMsg.includes('birthYear')) {
        apiErrors.set('add-child-birthYear', errorMsg);
      } else if (errorMsg.includes('Birth month') || errorMsg.includes('birthMonth')) {
        apiErrors.set('add-child-birthMonth', errorMsg);
      } else if (errorMsg.includes('age') || errorMsg.includes('18 years')) {
        apiErrors.set('add-child-birthYear', t('validation.childTooOld'));
      } else if (errorMsg.includes('inactive') || errorMsg.includes('not active')) {
        apiErrors.set('add-child-firstName', t('toast.familyPendingCannotAddMembers'));
      } else if (errorMsg.includes('more than')) {
        apiErrors.set('add-child-firstName', t('toast.familyTooManyChildren'));
      } else if (errorMsg) {
        apiErrors.set('add-child-firstName', errorMsg);
      } else {
        apiErrors.set('add-child-firstName', t('toast.failedToAddChild'));
      }

      setFormErrors(apiErrors);
      if (apiErrors.size > 0) {
        const firstErrorId = apiErrors.keys().next().value as string;
        scrollToError(firstErrorId);
      }
    }
  };

  const hasChanges = () => {
    return (
      formData.firstName !== '' ||
      formData.lastName !== '' ||
      formData.hasSpecialNeeds !== false ||
      formData.consentForPhoto !== false
    );
  };

  const isValid = () => {
    return (
      formData.firstName.trim().length >= 2 &&
      formData.lastName.trim().length >= 2 &&
      formData.birthYear &&
      formData.birthMonth
    );
  };

  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowConfirmDialog(true);
    } else {
      setIsExpanded(false);
    }
  };

  const handleConfirmDiscard = () => {
    setFormData(initialFormData);
    setShowConfirmDialog(false);
    setIsExpanded(false);
  };

  const genderOptions = getGenderOptions(t);
  const monthOptions = getMonthOptions(t);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 20 }, (_, i) => currentYear - i);

  if (!isExpanded) {
    return (
      <Card
        className="border-dashed border-2 border-[var(--color-neutral-300)] hover:border-[var(--color-primary-400)] cursor-pointer transition-colors"
        onClick={() => setIsExpanded(true)}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-2 text-[var(--color-neutral-500)] hover:text-[var(--color-primary-600)]">
            <Plus className="h-5 w-5" />
            <span className="font-medium">{t('buttons.addChild')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="ring-2 ring-[var(--color-primary-500)]">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Baby className="h-5 w-5 text-[var(--color-primary-700)]" />
            <span className="font-medium text-sm sm:text-base">{t('buttons.addChild')}</span>
          </div>
          <div className="flex gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={handleCancelClick} disabled={addChild.isPending}>
              <X className="h-4 w-4 mr-1" />
              {t('profile.cancel')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={addChild.isPending || !isValid()}>
              <Save className="h-4 w-4 mr-1" />
              {addChild.isPending ? t('profile.adding') : t('buttons.add')}
            </Button>
          </div>
        </div>

        {formErrors.size > 0 && (
          <div
            id="error-banner-child"
            className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
            role="alert"
            aria-live="polite"
          >
            <div className="flex gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                  {t('validation.childValidationFailed')}
                </h3>
                <div className="text-xs sm:text-sm text-red-700 space-y-1">
                  {Array.from(formErrors.values())
                    .slice(0, 5)
                    .map((error, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 break-words">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>{error}</span>
                      </div>
                    ))}
                  {formErrors.size > 5 && (
                    <div className="text-xs sm:text-sm text-red-700 mt-2">
                      {t('validation.andMore', { count: formErrors.size - 5 })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirmDialog}
          title={t('profile.discardChanges')}
          message={t('profile.discardConfirm')}
          onConfirm={handleConfirmDiscard}
          onCancel={() => setShowConfirmDialog(false)}
        />

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <Label htmlFor="add-child-firstName" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.firstName')}
              </Label>
              <Input
                id="add-child-firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder={t('placeholders.firstName')}
                className={`mt-1.5 text-base ${getErrorClass('add-child-firstName')}`}
              />
              {getErrorMessage('add-child-firstName') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-child-firstName')}</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="add-child-lastName" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.lastName')}
              </Label>
              <Input
                id="add-child-lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder={t('placeholders.lastName')}
                className={`mt-1.5 text-base ${getErrorClass('add-child-lastName')}`}
              />
              {getErrorMessage('add-child-lastName') && (
                <div className="mt-1 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{getErrorMessage('add-child-lastName')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            <div className="sm:col-span-2 md:col-span-1">
              <Label htmlFor="add-child-gender" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.gender')}
              </Label>
              <select
                id="add-child-gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                className="mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base"
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="add-child-birthMonth" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.birthMonth')}
              </Label>
              <select
                id="add-child-birthMonth"
                value={formData.birthMonth}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthMonth: parseInt(e.target.value),
                  })
                }
                className="mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base"
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="add-child-birthYear" className="text-sm font-medium min-h-[2.5rem] flex items-end pb-1">
                {t('profile.birthYear')}
              </Label>
              <select
                id="add-child-birthYear"
                value={formData.birthYear}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    birthYear: parseInt(e.target.value),
                  })
                }
                className="mt-1.5 w-full px-3 py-2.5 border rounded-md bg-white text-base"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('profile.specialNeeds')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hasSpecialNeeds: true })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.hasSpecialNeeds
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-5 w-5 mx-auto mb-1.5 ${formData.hasSpecialNeeds ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hasSpecialNeeds: false })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.hasSpecialNeeds
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-5 w-5 mx-auto mb-1.5 ${!formData.hasSpecialNeeds ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('photoRelease.title')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: true })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.consentForPhoto
                    ? 'border-[var(--color-success-500)] bg-[var(--color-success-100)] text-[var(--color-success-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-5 w-5 mx-auto mb-1.5 ${formData.consentForPhoto ? 'text-[var(--color-success-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, consentForPhoto: false })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.consentForPhoto
                    ? 'border-[var(--color-error-500)] bg-[var(--color-error-100)] text-[var(--color-error-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-5 w-5 mx-auto mb-1.5 ${!formData.consentForPhoto ? 'text-[var(--color-error-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
